import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box, Typography, Button, IconButton, CardMedia, Dialog, DialogContent, DialogActions
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { API_URLS } from '../config/api';
import { useCart } from '../contexts/CartContext';

function OrderPage() {
  const navigate = useNavigate();
  const [isSubmittingOrder, setIsSubmittingOrder] = useState(false);
  const [successDialogOpen, setSuccessDialogOpen] = useState(false);
  
  // 使用全局购物车状态
  const { products, selectedTableNumber, getCartItems, clearCart, getOpenNum } = useCart();

  // 图片占位图
  const placeholderImg = 'data:image/svg+xml;utf8,<svg width="120" height="120" xmlns="http://www.w3.org/2000/svg"><rect width="120" height="120" fill="%23f0f0f0"/></svg>';

  // 处理成功弹窗关闭
  const handleSuccessDialogClose = () => {
    setSuccessDialogOpen(false);
    navigate('/', { replace: true }); // 返回首页并清空历史记录
  };

  // 计算总价和数量
  const cartItems = getCartItems();
  const totalPrice = cartItems.reduce((sum, item) => {
    return sum + (Number(item.price) * item.count);
  }, 0);
  const totalCount = cartItems.reduce((sum, item) => sum + item.count, 0);

  // 提交订单
  const submitOrder = async () => {
    if (!selectedTableNumber || cartItems.length === 0) {
      alert('请选择桌台号并添加商品');
      return;
    }

    setIsSubmittingOrder(true);
    try {
      // 获取当前桌号的openNum
      const openNum = getOpenNum(selectedTableNumber);
      
      const orderContentList = cartItems.map(item => ({
        title: item.title,
        price: String(item.price),
        num: String(item.count),
        unit: item.unit || '', // 添加unit参数，如果没有unit则为空字符串
        product: item.id, // 添加商品id
        addTime: item.addTime || '' // 添加商品添加时间
      }));

      const orderData = {
        tableNum: selectedTableNumber,
        openNum: openNum || '', // 添加openNum字段
        orderContentList: orderContentList
      };

      // 临时调试：打印订单数据
      console.log('提交订单数据:', JSON.stringify(orderData, null, 2));

      const response = await fetch(API_URLS.PRINT_ORDER, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(orderData),
      });

      const result = await response.json();
      
      if (result.code === 200) {
        clearCart(); // 清空购物车
        setSuccessDialogOpen(true); // 显示成功弹窗
      } else {
        alert(`订单提交失败：${result.message || '未知错误'}`);
      }
    } catch (error) {
      console.error('提交订单失败:', error);
      alert('网络错误，请稍后重试');
    } finally {
      setIsSubmittingOrder(false);
    }
  };

  // 如果没有必要的数据，显示错误信息
  if (!selectedTableNumber) {
    return (
      <Box sx={{ height: '100vh', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
        <Typography sx={{ fontSize: 18, color: '#666', mb: 2 }}>
          请先选择桌号并添加商品
        </Typography>
        <Button 
          onClick={() => navigate('/table')}
          sx={{ 
            bgcolor: '#333', 
            color: '#fff',
            '&:hover': { bgcolor: '#555' }
          }}
        >
          返回桌台页面
        </Button>
      </Box>
    );
  }

  return (
    <Box sx={{ height: '100vh', display: 'flex', flexDirection: 'column', bgcolor: '#fff' }}>
      {/* 顶部标题栏 */}
      <Box sx={{ 
        display: 'flex', 
        alignItems: 'center',
        p: 2,
        borderBottom: '1px solid #e0e0e0',
        bgcolor: '#fff',
        position: 'sticky',
        top: 0,
        zIndex: 1000
      }}>
        <IconButton 
          onClick={() => navigate(-1)}
          sx={{ mr: 1 }}
        >
          <ArrowBackIcon />
        </IconButton>
        <Typography sx={{ fontSize: 18, fontWeight: 'bold', color: '#333' }}>
          已选商品
        </Typography>
      </Box>

      {/* 订单详情内容 */}
      <Box sx={{ flex: 1, p: 2, overflowY: 'auto' }}>
        {/* 桌台号 */}
        <Box sx={{ mb: 3 }}>
          <Typography sx={{ fontSize: 16, fontWeight: 'bold', color: '#333', mb: 1 }}>
            桌号：{selectedTableNumber}
          </Typography>
        </Box>

        {/* 待结算商品标题 */}
        <Typography sx={{ 
          fontSize: 16, 
          fontWeight: 'bold', 
          color: '#333', 
          mb: 2 
        }}>
          待结算商品
        </Typography>

        {/* 商品列表 */}
        <Box sx={{ mb: 3 }}>
          {cartItems.length > 0 ? (
            cartItems.map(item => (
              <Box key={item.id} sx={{ 
                display: 'flex', 
                alignItems: 'center',
                py: 2,
                borderBottom: '1px solid #f0f0f0'
              }}>
                <CardMedia
                  component="img"
                  image={item.preview ? `${API_URLS.DOWNLOAD_IMAGE}?view=${item.preview}` : placeholderImg}
                  alt={item.title}
                  sx={{ 
                    width: 50, 
                    height: 50, 
                    objectFit: 'cover',
                    borderRadius: 1,
                    mr: 2
                  }}
                />
                
                <Box sx={{ flex: 1 }}>
                  <Box sx={{ 
                    display: 'inline-block',
                    bgcolor: '#e8f5e8',
                    color: '#4caf50',
                    px: 1,
                    py: 0.5,
                    borderRadius: 1,
                    fontSize: 12,
                    mb: 0.5
                  }}>
                    单品
                  </Box>
                  <Typography sx={{ 
                    fontSize: 16, 
                    fontWeight: 'bold', 
                    color: '#333',
                    mb: 0.5
                  }}>
                    {item.title}
                  </Typography>
                  <Typography sx={{ 
                    fontSize: 16, 
                    color: '#333'
                  }}>
                    ¥{item.price}{item.unit ? `/${item.unit}` : ''}
                  </Typography>
                </Box>
                
                <Typography sx={{ 
                  fontSize: 16,
                  color: '#333',
                  minWidth: 40,
                  textAlign: 'right'
                }}>
                  x{item.count}
                </Typography>
              </Box>
            ))
          ) : (
            <Box sx={{ textAlign: 'center', py: 4 }}>
              <Typography sx={{ color: '#999', fontSize: 16 }}>
                购物车是空的
              </Typography>
            </Box>
          )}
        </Box>

        {/* 合计信息 */}
        {cartItems.length > 0 && (
          <Box sx={{ 
            bgcolor: '#f8f8f8',
            p: 2,
            borderRadius: 2,
            mb: 3
          }}>
            <Box sx={{ 
              display: 'flex', 
              justifyContent: 'space-between', 
              alignItems: 'center',
              mb: 1
            }}>
              <Typography sx={{ fontSize: 16, color: '#666' }}>
                合计：{totalPrice.toFixed(2)} {totalCount}项 {totalCount}份
              </Typography>
            </Box>
          </Box>
        )}
      </Box>

      {/* 底部提交按钮 */}
      {cartItems.length > 0 && (
        <Box sx={{ 
          p: 2,
          borderTop: '1px solid #e0e0e0',
          bgcolor: '#fff'
        }}>
          <Box sx={{ 
            display: 'flex', 
            justifyContent: 'space-between', 
            alignItems: 'center',
            mb: 2
          }}>
            <Typography sx={{ fontSize: 18, fontWeight: 'bold', color: '#333' }}>
              合计{totalCount}件，支付金额：¥{totalPrice.toFixed(2)}
            </Typography>
          </Box>
          
          <Button
            onClick={submitOrder}
            disabled={isSubmittingOrder}
            sx={{
              width: '100%',
              bgcolor: '#333',
              color: '#fff',
              borderRadius: 3,
              py: 1.5,
              fontSize: 16,
              fontWeight: 'bold',
              '&:hover': {
                bgcolor: '#555'
              },
              '&:disabled': {
                bgcolor: '#ccc'
              }
            }}
          >
            {isSubmittingOrder ? '提交中...' : '提交订单'}
          </Button>
        </Box>
      )}

      {/* 订单提交成功弹窗 */}
      <Dialog
        open={successDialogOpen}
        onClose={handleSuccessDialogClose}
        maxWidth="sm"
        fullWidth
        sx={{
          '& .MuiDialog-paper': {
            borderRadius: 3,
            textAlign: 'center',
            p: 2
          }
        }}
      >
        <DialogContent sx={{ pt: 4, pb: 2 }}>
          <CheckCircleIcon 
            sx={{ 
              fontSize: 80, 
              color: '#4caf50',
              mb: 2
            }} 
          />
          <Typography sx={{ 
            fontSize: 24, 
            fontWeight: 'bold', 
            color: '#333',
            mb: 1
          }}>
            订单提交成功！
          </Typography>
          <Typography sx={{ 
            fontSize: 16, 
            color: '#666',
            mb: 2
          }}>
            您的订单已成功提交，请耐心等待出餐
          </Typography>
          <Typography sx={{ 
            fontSize: 14, 
            color: '#999'
          }}>
            桌号：{selectedTableNumber}
          </Typography>
        </DialogContent>
        <DialogActions sx={{ justifyContent: 'center', pb: 3 }}>
          <Button
            onClick={handleSuccessDialogClose}
            sx={{
              bgcolor: '#333',
              color: '#fff',
              borderRadius: 3,
              px: 4,
              py: 1.5,
              fontSize: 16,
              fontWeight: 'bold',
              minWidth: 120,
              '&:hover': {
                bgcolor: '#555'
              }
            }}
          >
            返回主页
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default OrderPage; 